import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Coach, CoachPreferences, CoachDashboard } from '@/types'

interface CoachStore {
  // Coach data
  coach: Coach | null
  preferences: CoachPreferences | null
  dashboard: CoachDashboard | null
  
  // Authentication
  accessToken: string | null
  isInitialized: boolean
  
  // UI state
  sidebarOpen: boolean
  currentPage: string
  
  // Actions
  setCoach: (coach: Coach) => void
  updateCoach: (updates: Partial<Coach>) => void
  setPreferences: (preferences: CoachPreferences) => void
  updatePreferences: (updates: Partial<CoachPreferences>) => void
  setDashboard: (dashboard: CoachDashboard) => void
  setAccessToken: (token: string | null) => void
  setInitialized: (initialized: boolean) => void
  setSidebarOpen: (open: boolean) => void
  setCurrentPage: (page: string) => void
  clearCoach: () => void
}

export const useCoachStore = create<CoachStore>()(
  persist(
    (set, get) => ({
      // Initial state
      coach: null,
      preferences: null,
      dashboard: null,
      accessToken: null,
      isInitialized: false,
      sidebarOpen: false,
      currentPage: 'dashboard',
      
      // Actions
      setCoach: (coach) => set({ coach, isInitialized: true }),
      
      updateCoach: (updates) => {
        const currentCoach = get().coach
        if (currentCoach) {
          set({ 
            coach: { 
              ...currentCoach, 
              ...updates, 
              updatedAt: new Date() 
            } 
          })
        }
      },
      
      setPreferences: (preferences) => set({ preferences }),
      
      updatePreferences: (updates) => {
        const currentPreferences = get().preferences
        if (currentPreferences) {
          set({ preferences: { ...currentPreferences, ...updates } })
        }
      },
      
      setDashboard: (dashboard) => set({ dashboard }),
      
      setAccessToken: (accessToken) => set({ accessToken }),
      
      setInitialized: (isInitialized) => set({ isInitialized }),
      
      setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
      
      setCurrentPage: (currentPage) => set({ currentPage }),
      
      clearCoach: () => set({
        coach: null,
        preferences: null,
        dashboard: null,
        accessToken: null,
        isInitialized: false,
      }),
    }),
    {
      name: 'coach-storage',
      partialize: (state) => ({
        coach: state.coach,
        preferences: state.preferences,
        // Don't persist sensitive data
        accessToken: null,
        isInitialized: state.isInitialized,
        sidebarOpen: state.sidebarOpen,
        currentPage: state.currentPage,
      }),
    }
  )
)